import { useRef, useMemo, Suspense, useEffect } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Float, Stars } from "@react-three/drei";
import * as THREE from "three";

// ── Dashed edge outline for a box ─────────────────────────────────────────────
function DashedBox({ w = 1.56, h = 0.162, d = 1.56, color = "#6EE7B7", dashSize = 0.12, gapSize = 0.07 }) {
  const ref = useRef();
  const geo = useMemo(() => new THREE.EdgesGeometry(new THREE.BoxGeometry(w, h, d)), [w, h, d]);
  useEffect(() => { if (ref.current) ref.current.computeLineDistances(); }, []);
  return (
    <lineSegments ref={ref} geometry={geo}>
      <lineDashedMaterial color={color} dashSize={dashSize} gapSize={gapSize} />
    </lineSegments>
  );
}

// ── Shared scroll state (written from page, read inside Canvas) ───────────────
export const scrollState = { progress: 0 };

// ── Lights ────────────────────────────────────────────────────────────────────
function Lights() {
  const green  = useRef();
  const blue   = useRef();

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (green.current) {
      green.current.position.x = Math.sin(t * 0.4) * 4;
      green.current.position.z = Math.cos(t * 0.4) * 4;
    }
    if (blue.current) {
      blue.current.position.x = Math.cos(t * 0.3) * 5;
      blue.current.position.z = Math.sin(t * 0.3) * 3;
    }
  });

  return (
    <>
      <ambientLight intensity={0.25} />
      <pointLight ref={green} position={[3, 2, 3]}  intensity={3}   color="#10B77F" />
      <pointLight ref={blue}  position={[-4, 1, 2]} intensity={2.2} color="#627EEA" />
      <pointLight position={[0, 8, 0]}  intensity={0.8} color="#ffffff"  />
      <pointLight position={[0, -4, 2]} intensity={1.4} color="#0D1117"  />
      <directionalLight position={[5, 5, 5]} intensity={0.4} castShadow />
    </>
  );
}

// ── Main hero centrepiece — stacked tiles (matches AlphaStack logo icon) ──────
// 5 flat square slabs stacked like layers, smaller toward the top
function LogoStack() {
  const group = useRef();

  // 5 tiles: all same size, equally spaced
  const TILES = [
    { y: -0.80 },
    { y: -0.38 },
    { y:  0.04 },
    { y:  0.46 },
    { y:  0.88 },
  ];

  // store base tilt
  const baseRotation = useMemo(
    () => new THREE.Euler(-0.65, 0.35, 0.12),
    []
  );

  useFrame((state, delta) => {
    const p = scrollState.progress;

    if (group.current) {
      // isometric-style: tilt forward so top faces are visible, slow Y spin
      group.current.rotation.x =  0.72;
      group.current.rotation.z =  0.0;
      group.current.rotation.y += delta * 0.10;

      const sc = THREE.MathUtils.clamp(1 - p * 1.6, 0.12, 1);
      group.current.scale.setScalar(sc);
      group.current.position.y = THREE.MathUtils.lerp(
        0.2,
        -4,
        Math.min(p * 2, 1)
      );
    }
  });

  return (
    <group
      ref={group}
      position={[1.7, 0.4, 0]}
      rotation={[0.72, 0.78, 0]}
    >
      {TILES.map(({ y }, i) => (
        <group key={i} position={[0, y, 0]}>
          {/* Top face — solid green fill */}
          <mesh rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[1.50, 1.50]} />
            <meshStandardMaterial
              color="#10B77F"
              emissive="#10B77F"
              emissiveIntensity={0.55 + i * 0.08}
              transparent
              opacity={0.85}
              roughness={0.1}
              side={THREE.DoubleSide}
            />
          </mesh>

        </group>
      ))}

      {/* Ambient glow */}
      <mesh position={[0, -1.1, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[1.1, 32]} />
        <meshBasicMaterial
          color="#10B77F"
          transparent
          opacity={0.08}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}

// ── Gold coin ─────────────────────────────────────────────────────────────────
function GoldCoin() {
  const group = useRef();

  useFrame((state, delta) => {
    const p = scrollState.progress;
    if (group.current) {
      group.current.rotation.y += delta * 0.55;
      group.current.rotation.z = Math.sin(state.clock.elapsedTime * 0.45) * 0.08;
      group.current.position.x = THREE.MathUtils.lerp(-2.6, -5, Math.min(p * 1.5, 1));
      group.current.position.y = THREE.MathUtils.lerp(-0.8, -4, Math.min(p * 2, 1));
    }
  });

  return (
    <Float speed={1.4} rotationIntensity={0.25} floatIntensity={0.6}>
      <group ref={group} position={[-2.6, -0.8, 0.8]}>
        {/* Main disc */}
        <mesh>
          <cylinderGeometry args={[0.9, 0.9, 0.1, 72]} />
          <meshStandardMaterial color="#F59E0B" metalness={0.95} roughness={0.06} />
        </mesh>
        {/* Rim */}
        <mesh position={[0, 0, 0]}>
          <torusGeometry args={[0.9, 0.05, 8, 72]} />
          <meshStandardMaterial color="#D97706" metalness={0.9} roughness={0.1} />
        </mesh>
        {/* Face sheen */}
        <mesh position={[0, 0.055, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.72, 48]} />
          <meshStandardMaterial color="#FCD34D" metalness={0.85} roughness={0.12} />
        </mesh>
      </group>
    </Float>
  );
}

// ── Crystal shards ────────────────────────────────────────────────────────────
function Crystals() {
  const configs = useMemo(() => [
    { pos: [ 3.8,  1.8, -1.2], scale: 0.28, color: "#627EEA", wire: false, speed: 0.9  },
    { pos: [-3.6,  0.4, -2.0], scale: 0.22, color: "#A78BFA", wire: true,  speed: 1.1  },
    { pos: [ 3.0, -2.2, -1.8], scale: 0.34, color: "#627EEA", wire: false, speed: 0.75 },
    { pos: [-2.2,  2.8, -0.8], scale: 0.19, color: "#10B77F", wire: true,  speed: 1.3  },
    { pos: [ 4.5, -1.2, -2.5], scale: 0.26, color: "#A78BFA", wire: false, speed: 1.0  },
    { pos: [-3.2, -1.8, -2.2], scale: 0.21, color: "#627EEA", wire: true,  speed: 0.85 },
    { pos: [ 2.1,  3.2, -1.5], scale: 0.16, color: "#10B77F", wire: false, speed: 1.2  },
  ], []);

  return (
    <>
      {configs.map((c, i) => (
        <Float key={i} speed={c.speed} rotationIntensity={0.5} floatIntensity={0.35}>
          <mesh position={c.pos} scale={c.scale}>
            <icosahedronGeometry args={[1, 0]} />
            <meshStandardMaterial
              color={c.color}
              emissive={c.color}
              emissiveIntensity={c.wire ? 0.2 : 0.35}
              metalness={0.25}
              roughness={0.65}
              wireframe={c.wire}
            />
          </mesh>
        </Float>
      ))}
    </>
  );
}

// ── Particle field ────────────────────────────────────────────────────────────
function ParticleField() {
  const COUNT = 1400;

  const { geo, mat } = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const pos = new Float32Array(COUNT * 3);
    const col = new Float32Array(COUNT * 3);
    const colors = [
      new THREE.Color("#10B77F"),
      new THREE.Color("#627EEA"),
      new THREE.Color("#ffffff"),
      new THREE.Color("#A78BFA"),
    ];

    for (let i = 0; i < COUNT; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi   = Math.acos(2 * Math.random() - 1);
      const r     = 4.5 + Math.random() * 8;
      pos[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) - 1;
      pos[i * 3 + 2] = r * Math.cos(phi) - 2;

      const c = colors[Math.floor(Math.random() * colors.length)];
      col[i * 3]     = c.r;
      col[i * 3 + 1] = c.g;
      col[i * 3 + 2] = c.b;
    }

    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    geo.setAttribute("color",    new THREE.BufferAttribute(col, 3));

    const mat = new THREE.PointsMaterial({
      size: 0.034,
      vertexColors: true,
      transparent: true,
      opacity: 0.72,
      sizeAttenuation: true,
    });

    return { geo, mat };
  }, []);

  const ref = useRef();

  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.y = state.clock.elapsedTime * 0.018;
      ref.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.009) * 0.12;
    }
  });

  return <points ref={ref} geometry={geo} material={mat} />;
}

// ── Floating ring accent ──────────────────────────────────────────────────────
function RingAccent() {
  const ref = useRef();

  useFrame((_state, delta) => {
    const p = scrollState.progress;
    if (ref.current) {
      ref.current.rotation.x += delta * 0.06;
      ref.current.rotation.z += delta * 0.04;
      ref.current.position.x = THREE.MathUtils.lerp(2.8, 5, Math.min(p * 2, 1));
    }
  });

  return (
    <mesh ref={ref} position={[2.8, 1, -1.5]}>
      <torusGeometry args={[1.2, 0.025, 6, 80]} />
      <meshBasicMaterial color="#10B77F" transparent opacity={0.35} />
    </mesh>
  );
}

// ── Scroll-driven camera ──────────────────────────────────────────────────────
function CameraRig() {
  const { camera } = useThree();
  const target = useMemo(() => new THREE.Vector3(0, 0, 7), []);

  useFrame((state, delta) => {
    const p = scrollState.progress;

    // Hero (0–30%): stable, subtle mouse parallax
    // Features (30–65%): shift left and back
    // CTA (65–100%): pull back high
    let tx, ty, tz;

    if (p < 0.3) {
      tx = 0;
      ty = 0;
      tz = 7;
    } else if (p < 0.65) {
      const n = (p - 0.3) / 0.35;
      tx = THREE.MathUtils.lerp(0, -1.4, n);
      ty = THREE.MathUtils.lerp(0, -0.4, n);
      tz = THREE.MathUtils.lerp(7, 9, n);
    } else {
      const n = (p - 0.65) / 0.35;
      tx = THREE.MathUtils.lerp(-1.4, 0.5, n);
      ty = THREE.MathUtils.lerp(-0.4, -1.2, n);
      tz = THREE.MathUtils.lerp(9, 11.5, n);
    }

    target.set(tx, ty, tz);
    camera.position.lerp(target, delta * 1.6);

    // Subtle mouse tilt
    camera.rotation.x += (state.pointer.y * 0.02 - camera.rotation.x) * 0.04;
    camera.rotation.y += (-state.pointer.x * 0.025 - camera.rotation.y) * 0.04;
  });

  return null;
}

// ── Scene content (inside Canvas) ─────────────────────────────────────────────
function SceneContent() {
  return (
    <>
      <Lights />
      <CameraRig />
      <Suspense fallback={null}>
        <LogoStack />
        <GoldCoin />
        <Crystals />
        <ParticleField />
        <RingAccent />
        <Stars
          radius={90}
          depth={60}
          count={2800}
          factor={3.5}
          saturation={0.1}
          fade
          speed={0.4}
        />
      </Suspense>
    </>
  );
}

// ── Canvas export ─────────────────────────────────────────────────────────────
export default function Scene3D() {
  return (
    <Canvas
      camera={{ position: [0, 0, 7], fov: 55, near: 0.1, far: 200 }}
      gl={{
        antialias: true,
        alpha: true,
        powerPreference: "high-performance",
        toneMapping: THREE.ACESFilmicToneMapping,
        toneMappingExposure: 1.1,
      }}
      style={{
        position: "fixed",
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 0,
        pointerEvents: "none",
      }}
      dpr={[1, 1.5]}
    >
      <SceneContent />
    </Canvas>
  );
}
